import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconBarcode,
  IconPrinter,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useItems } from '../items/itemsApi';
import { LabelSheet, type LabelEntry } from './LabelSheet';
import type { ItemDto } from '../items/types';

// Phase 4A label printing. Layout:
//   Top half: catalog picker with search + Add button per item.
//   Bottom half: selected labels list (with per-label quantity and a
//                CODE128/EAN13 picker) and a Print button.
//
// "Print" calls window.print() — the actual print is the browser's
// native dialog. CSS @media print rules hide the entire app shell so
// only the label sheet survives onto the page.

interface Selection {
  /** Stable key to dedupe + iterate. cartLineId equivalent. */
  key: string;
  item: ItemDto;
  /** Which of the item's barcodes (or its SKU fallback) the operator
   *  picked for this label. */
  value: string;
  format: 'CODE128' | 'EAN13';
  quantity: number;
}

function defaultBarcodeFor(item: ItemDto): { value: string; format: 'CODE128' | 'EAN13' } {
  const primary = item.barcodes.find((b) => b.isPrimary) ?? item.barcodes[0];
  if (primary) {
    // EAN-13 if the barcode is exactly 12 or 13 digits; CODE128 otherwise.
    const isEan = /^\d{12,13}$/.test(primary.barcode);
    return { value: primary.barcode, format: isEan ? 'EAN13' : 'CODE128' };
  }
  // No registered barcode → fall back to SKU as CODE128 so the operator
  // can still sticker the item. Scanning the sticker resolves via the
  // cashier's item search (SKU + name + barcode all share one box).
  return { value: item.sku, format: 'CODE128' };
}

export function LabelsPage() {
  const { t, i18n } = useTranslation();
  const { data: items, isLoading } = useItems();
  const [search, setSearch] = useState('');
  const [selections, setSelections] = useState<Selection[]>([]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items ?? []).filter((i) => {
      if (!q) return true;
      if (i.nameAr.toLowerCase().includes(q)) return true;
      if (i.nameEn?.toLowerCase().includes(q)) return true;
      if (i.sku.toLowerCase().includes(q)) return true;
      return i.barcodes.some((b) => b.barcode.toLowerCase().includes(q));
    });
  }, [items, search]);

  const addItem = (item: ItemDto) => {
    const def = defaultBarcodeFor(item);
    setSelections((prev) => {
      // If already on the list, just bump quantity.
      const existing = prev.find(
        (s) => s.item.itemId === item.itemId && s.value === def.value,
      );
      if (existing) {
        return prev.map((s) =>
          s === existing ? { ...s, quantity: s.quantity + 1 } : s,
        );
      }
      return [
        ...prev,
        {
          key: `${item.itemId}-${def.value}-${Date.now()}`,
          item,
          value: def.value,
          format: def.format,
          quantity: 1,
        },
      ];
    });
  };

  const updateSelection = (key: string, patch: Partial<Selection>) =>
    setSelections((prev) =>
      prev.map((s) => (s.key === key ? { ...s, ...patch } : s)),
    );

  const removeSelection = (key: string) =>
    setSelections((prev) => prev.filter((s) => s.key !== key));

  const clearAll = () => setSelections([]);

  // Flatten selections × quantity into the sheet entries.
  const sheetEntries = useMemo<LabelEntry[]>(() => {
    const out: LabelEntry[] = [];
    for (const s of selections) {
      const qty = Math.max(1, Math.min(200, Math.floor(s.quantity || 1)));
      for (let i = 0; i < qty; i++) {
        out.push({ item: s.item, value: s.value, format: s.format });
      }
    }
    return out;
  }, [selections]);

  const handlePrint = () => {
    // Defer to next tick so any pending state flushes into the SVG
    // before the print snapshot is taken.
    setTimeout(() => window.print(), 50);
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('labels.page.title')}
        subtitle={t('labels.page.subtitle')}
        actions={
          <Button
            leftSection={<IconPrinter size={16} stroke={1.8} />}
            onClick={handlePrint}
            disabled={sheetEntries.length === 0}
          >
            {t('labels.page.print', { count: sheetEntries.length })}
          </Button>
        }
      />

      {/* Two-column layout: catalog picker on the left, selection
          list on the right. Stacks vertically below md. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Paper p={isLoading || filtered.length === 0 ? 'lg' : 0}>
          <div className="p-3 border-b border-[var(--wheels-border)]">
            <TextInput
              placeholder={t('items.search.placeholder')}
              leftSection={<IconSearch size={16} stroke={1.6} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
          {isLoading ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              {t('common.loading')}
            </Text>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<IconBarcode size={22} stroke={1.6} />}
              title={t('labels.empty.noItemsTitle')}
              description={t('labels.empty.noItemsBody')}
            />
          ) : (
            <ScrollArea h={420}>
              <Table verticalSpacing="xs">
                <Table.Tbody>
                  {filtered.map((it) => {
                    const def = defaultBarcodeFor(it);
                    return (
                      <Table.Tr key={it.itemId}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {i18n.language === 'ar' ? it.nameAr : (it.nameEn ?? it.nameAr)}
                          </Text>
                          <Text size="xs" c="dimmed" className="font-mono">
                            {it.sku}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {it.barcodes.length === 0 ? (
                            <Badge size="xs" variant="light" color="gray">
                              {t('labels.picker.noBarcode')}
                            </Badge>
                          ) : (
                            <Text size="xs" className="font-mono" c="dimmed">
                              {def.value}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td style={{ width: 60 }}>
                          <Button size="xs" variant="light" onClick={() => addItem(it)}>
                            {t('labels.picker.add')}
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>

        <Paper p={selections.length === 0 ? 'lg' : 'md'}>
          <Group justify="space-between" mb={selections.length === 0 ? 0 : 'sm'}>
            <div>
              <Text fw={500} c="var(--wheels-ink-strong)">
                {t('labels.selection.title')}
              </Text>
              <Text size="xs" c="dimmed">
                {t('labels.selection.subtitle', { count: sheetEntries.length })}
              </Text>
            </div>
            {selections.length > 0 && (
              <Button variant="subtle" size="xs" color="red" onClick={clearAll}>
                {t('labels.selection.clearAll')}
              </Button>
            )}
          </Group>

          {selections.length === 0 ? (
            <EmptyState
              icon={<IconBarcode size={22} stroke={1.6} />}
              title={t('labels.empty.noSelectionTitle')}
              description={t('labels.empty.noSelectionBody')}
            />
          ) : (
            <Stack gap="xs">
              {selections.map((s) => (
                <div
                  key={s.key}
                  className="border border-[var(--wheels-border)] rounded-md p-2 flex flex-col gap-2"
                >
                  <Group justify="space-between" wrap="nowrap">
                    <div className="flex-1 min-w-0">
                      <Text size="sm" fw={500} lineClamp={1}>
                        {i18n.language === 'ar' ? s.item.nameAr : (s.item.nameEn ?? s.item.nameAr)}
                      </Text>
                      <Text size="xs" c="dimmed" className="font-mono">
                        {s.value}
                      </Text>
                    </div>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => removeSelection(s.key)}
                      aria-label={t('common.delete')}
                    >
                      <IconTrash size={14} stroke={1.6} />
                    </ActionIcon>
                  </Group>
                  <Group gap="sm" wrap="nowrap">
                    <SegmentedControl
                      size="xs"
                      value={s.format}
                      onChange={(v) =>
                        updateSelection(s.key, { format: v as 'CODE128' | 'EAN13' })
                      }
                      data={[
                        { label: 'CODE128', value: 'CODE128' },
                        { label: 'EAN-13', value: 'EAN13' },
                      ]}
                    />
                    <NumberInput
                      size="xs"
                      min={1}
                      max={200}
                      value={s.quantity}
                      onChange={(v) =>
                        updateSelection(s.key, {
                          quantity: typeof v === 'number' ? v : 1,
                        })
                      }
                      style={{ width: 90 }}
                    />
                  </Group>
                </div>
              ))}
            </Stack>
          )}
        </Paper>
      </div>

      {/* Print sheet. Hidden on screen via CSS — only paints on print. */}
      <div className="label-sheet-screen-hidden">
        <LabelSheet labels={sheetEntries} />
      </div>
    </Stack>
  );
}
